import classNames from 'classnames'
import {Suspense, useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {ImgButton} from '../../components/core/button'
import ContentLoader from '../../components/core/loader'
import Footer from '../../components/layouts/footer'
import PhoneCallTemplatesTable from '../../components/pages/phoneCalls/PhoneCallTemplatesTable'
import {PlusIcon} from '../../icons'
import {routes} from '../../routes'
import Customer from '../../utils/customer'
import {envConfig} from '../../utils/env'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import NavigationHelper from '../../utils/navigation'
import ContentHeader from '../../components/layouts/header'
import {deletePhoneCallTemplate, getAzureVoicesList, getPhoneCallTemplates} from '../../stores/phone-call'
import Confirm from '../../components/core/confirm'
import {setConfirmIsActive} from '../../stores/alert'
import EmptyContent from '../resources/EmptyContent'

const PhoneCalls = () => {
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const phoneCallTemplates = useSelector(state => state.phone_call.phoneCallTemplates)
    const isProcessing = useSelector(state => state.phone_call.isFetching)
    const voicesList = useSelector(state => state.phone_call.voicesList)

    const [deletedPhoneCallId, setDeletedPhoneCallId] = useState('')
    const [isDeleting, setIsDeleting] = useState(false)
    const [allPhoneCalls, setAllPhoneCalls] = useState(phoneCallTemplates)

    const text = useNamespaceTranslations('phone-calls')
    const {isReadOnlyModeEnabled} = envConfig
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const navigateToEditPage = phoneCallId => navigate(NavigationHelper.getNavigationUrl(NavigationHelper.getLocalizedPath(routes.update_phone_call.paths), ':id', phoneCallId))

    const prepareToDelete = phoneCallId => {
        dispatch(setConfirmIsActive(true))
        setDeletedPhoneCallId(phoneCallId)
    }

    const handleDeletePhoneCall = async() => {
        setIsDeleting(true)
        const {success} = await dispatch(deletePhoneCallTemplate(deletedPhoneCallId))
        dispatch(setConfirmIsActive(false))
        setDeletedPhoneCallId('')
        if (success) {
            setAllPhoneCalls(allPhoneCalls?.length > 1 ? allPhoneCalls?.filter(phoneCall => phoneCall?.id !== deletedPhoneCallId) : null)
        }
        setIsDeleting(false)
    }

    useEffect(() => {
        dispatch(getPhoneCallTemplates())
        dispatch(getAzureVoicesList())
    }, [])

    useEffect(() => {
        setAllPhoneCalls(phoneCallTemplates)
    }, [phoneCallTemplates])

    return (
        <main className={classNames('main phone-calls', {
            dark: isDarkMode
        })}>
            <Confirm
                body={text.are_you_sure}
                callback={handleDeletePhoneCall}
                showLoader={isDeleting}
            />

            <div>
                <ContentHeader section={text.section} title={text.title}>
                    {Customer.isOwnTeam() && (
                        <ImgButton disabled={isReadOnlyModeEnabled || allPhoneCalls?.length === 1}
                            onClick={() => navigate(NavigationHelper.getLocalizedPath(routes.create_phone_call.paths))}>
                            {text.add_phone_call}
                            <span><PlusIcon/></span>
                        </ImgButton>
                    )}
                </ContentHeader>

                <div className='content-body'>
                    {isProcessing || allPhoneCalls?.length === 0
                        ? <ContentLoader/>
                        : allPhoneCalls && allPhoneCalls?.length > 0
                            ? <Suspense fallback={<ContentLoader/>}>
                                <PhoneCallTemplatesTable
                                    phoneCallTemplates={allPhoneCalls}
                                    isDarkMode={isDarkMode}
                                    text={text}
                                    voicesList={voicesList}
                                    onNavigateToEditPage={(phoneCallId) => navigateToEditPage(phoneCallId)}
                                    onPrepareToDelete={prepareToDelete}
                                    isAddBtnDisabled={!Customer.isOwnTeam()}/>
                            </Suspense>
                            : <>
                                {Customer.isOwnTeam() && <div className='search-filter-actions'>
                                    <span className='selected-actions'>
                                        <ImgButton disabled={isReadOnlyModeEnabled || allPhoneCalls?.length === 1}
                                            onClick={() => navigate(NavigationHelper.getLocalizedPath(routes.create_phone_call.paths))}
                                            className='responsive-add-button'>
                                            {text.add_phone_call}
                                            <span><PlusIcon/></span>
                                        </ImgButton>
                                    </span>
                                </div>}
                                <EmptyContent
                                    message={text.empty_content_text}
                                    hideButton={true}/>
                            </>
                    }
                </div>
            </div>

            <Footer/>
        </main>
    )
}

export default PhoneCalls
